import { useState, useEffect, useCallback, useRef } from 'react'
import Box from '@mui/material/Box'

const GAME_WIDTH = 800
const GAME_HEIGHT = 400
const GROUND_Y = 320
const GRAVITY = 0.8
const JUMP_FORCE = -14
const MOVE_SPEED = 5

const ZOMBIE_SPEED = 1.5
const BIRD_SPEED = 2.5
const ENEMY_HIT_RADIUS = 18

const COINS_TO_WIN = [10, 30, 20, 0]
const COIN_RADIUS = 18

const SWORD_RANGE = 50
const SWORD_DURATION = 12 // frames the swing lasts

const GRAPPLE_RANGE = 400
const GRAPPLE_PULL_SPEED = 6
const GRAPPLE_LAUNCH_VX = 8
const GRAPPLE_LAUNCH_VY = -16

const BOSS_SPEED = 1.2
const BOSS_SIZE = 80
const CRATER_WIDTH = 70

const GRENADE_FUSE = 180 // 3 seconds at 60fps
const GRENADE_THROW_VX = 6
const GRENADE_THROW_VY = -8
const GRENADE_BLAST_RADIUS = 120
const GRENADE_GRAVITY = 0.3

const LEVELS = [
  {
    name: 'Level 1 - Mountains',
    obstacleTypes: ['canyon', 'mountain', 'tall_mountain'],
    weapon: 'bow',
    hasEnemies: true,
    sky: { top: '#1a1a2e', bottom: '#16213e' },
    groundColor: '#4a7c2e',
    canyonWallColor: '#3d2b1f',
    stars: true,
  },
  {
    name: 'Level 2 - Desert',
    obstacleTypes: ['canyon', 'cactus', 'tall_cactus'],
    weapon: 'sword',
    hasEnemies: true,
    sky: { top: '#87CEEB', bottom: '#f0e68c' },
    groundColor: '#c2a366',
    canyonWallColor: '#8b6914',
    stars: false,
  },
  {
    name: 'Level 3 - Sunset Canyon',
    obstacleTypes: ['canyon', 'cactus', 'tall_cactus'],
    weapon: 'grapple',
    hasEnemies: false,
    sky: { top: '#4a1a6b', bottom: '#e8651a' },
    groundColor: '#8b5e3c',
    canyonWallColor: '#5c3a1e',
    stars: false,
  },
  {
    name: 'Level 4 - Boss Fight',
    obstacleTypes: [],
    weapon: 'grenade',
    hasEnemies: false,
    boss: true,
    fixedScreen: true,
    sky: { top: '#0d3b0d', bottom: '#1a5c1a' },
    groundColor: '#2d5a1e',
    canyonWallColor: '#1a3310',
    stars: false,
  },
]

function createObstacle(x, level) {
  const types = LEVELS[level].obstacleTypes
  const type = types[Math.floor(Math.random() * types.length)]
  return { x, type }
}

function generateEnemies(playerX) {
  const enemies = []
  for (let i = 0; i < 5; i++) {
    const x = playerX + 600 + Math.random() * 2000
    if (Math.random() < 0.5) {
      enemies.push({ x, y: GROUND_Y, type: 'zombie', dir: Math.random() < 0.5 ? 1 : -1 })
    } else {
      const birdY = 180 + Math.random() * 100
      enemies.push({ x, y: birdY, type: 'bird', dir: Math.random() < 0.5 ? 1 : -1 })
    }
  }
  return enemies
}

function generateCoins(startX) {
  const coins = []
  for (let i = 0; i < 15; i++) {
    const x = startX + 300 + Math.random() * 3000
    const y = GROUND_Y - 60 - Math.random() * 80
    coins.push({ x, y })
  }
  return coins
}

function getCactusTop(obs) {
  if (obs.type === 'tall_cactus') return { x: obs.x + 25, y: GROUND_Y - 75 }
  if (obs.type === 'cactus') return { x: obs.x + 25, y: GROUND_Y - 50 }
  return null
}

function generateObstacles(level) {
  const obs = []
  let x = 500
  for (let i = 0; i < 20; i++) {
    obs.push(createObstacle(x, level))
    x += 250 + Math.random() * 200
  }
  return obs
}

const RunGame = () => {
  const [gameState, setGameState] = useState('idle') // idle, running, dead, won, levelComplete
  const [, setScore] = useState(0)
  const [highScore, setHighScore] = useState(0)
  const playerX = useRef(0)
  const playerY = useRef(GROUND_Y)
  const velocityY = useRef(0)
  const isJumping = useRef(false)
  const facingRight = useRef(true)
  const obstacles = useRef([])
  const frameRef = useRef(null)
  const maxDistance = useRef(0)
  const gameStateRef = useRef('idle')
  const canvasRef = useRef(null)
  const keysDown = useRef({})
  const arrows = useRef([])
  const enemies = useRef([])
  const coins = useRef([])
  const coinCount = useRef(0)
  const level = useRef(0)
  const swordSwing = useRef(0) // frames remaining in swing
  const grapple = useRef(null) // { targetX, targetY } or null
  const boss = useRef(null)
  const craters = useRef([])
  const grenades = useRef([]) // { x, y, vx, vy, fuse }

  const initLevel = useCallback((lvl) => {
    level.current = lvl
    playerX.current = 0
    playerY.current = GROUND_Y
    velocityY.current = 0
    isJumping.current = false
    facingRight.current = true
    maxDistance.current = 0
    keysDown.current = {}
    arrows.current = []
    swordSwing.current = 0
    grapple.current = null
    craters.current = []
    grenades.current = []
    if (LEVELS[lvl].boss) {
      boss.current = {
        x: 600,
        phase: 'walking', // walking, winding_up, punching, recovering
        timer: 120,
        dir: -1,
        punchX: 0,
        fistY: 0, // for punch animation (0 = up, 1 = ground)
        hp: 10,
      }
      enemies.current = []
      coins.current = []
      coinCount.current = 0
      obstacles.current = []
    } else {
      boss.current = null
      enemies.current = LEVELS[lvl].hasEnemies ? generateEnemies(0) : []
      coins.current = generateCoins(0)
      coinCount.current = 0
      obstacles.current = generateObstacles(lvl)
    }
    setScore(0)
    setGameState('running')
    gameStateRef.current = 'running'
  }, [])

  const startGame = useCallback(() => {
    initLevel(level.current)
  }, [initLevel])

  const nextLevel = useCallback(() => {
    initLevel(level.current + 1)
  }, [initLevel])

  const die = useCallback(() => {
    setGameState('dead')
    gameStateRef.current = 'dead'
    const finalScore = Math.floor(maxDistance.current / 10)
    setScore(finalScore)
    setHighScore((prev) => Math.max(prev, finalScore))
  }, [])

  const win = useCallback(() => {
    if (level.current < LEVELS.length - 1) {
      setGameState('levelComplete')
      gameStateRef.current = 'levelComplete'
    } else {
      setGameState('won')
      gameStateRef.current = 'won'
    }
    const finalScore = Math.floor(maxDistance.current / 10)
    setScore(finalScore)
    setHighScore((prev) => Math.max(prev, finalScore))
  }, [])

  // keyboard controls
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.code === 'Space' || e.code === 'ArrowUp') {
        e.preventDefault()
        if (gameStateRef.current === 'levelComplete') {
          nextLevel()
          return
        }
        if (gameStateRef.current !== 'running') {
          startGame()
          return
        }
        if (gameStateRef.current === 'running' && !isJumping.current) {
          velocityY.current = JUMP_FORCE
          isJumping.current = true
        }
      }
      if (e.code === 'KeyC' && gameStateRef.current === 'running') {
        const lvl = LEVELS[level.current]
        if (lvl.weapon === 'bow') {
          arrows.current.push({
            x: playerX.current,
            y: playerY.current - 18,
            dir: facingRight.current ? 1 : -1,
          })
        } else if (lvl.weapon === 'sword' && swordSwing.current <= 0) {
          swordSwing.current = SWORD_DURATION
        } else if (lvl.weapon === 'grenade') {
          const dir = facingRight.current ? 1 : -1
          grenades.current.push({
            x: playerX.current,
            y: playerY.current - 18,
            vx: GRENADE_THROW_VX * dir,
            vy: GRENADE_THROW_VY,
            fuse: GRENADE_FUSE,
          })
        } else if (lvl.weapon === 'grapple' && !grapple.current) {
          // find nearest cactus in facing direction
          const dir = facingRight.current ? 1 : -1
          let best = null
          let bestDist = Infinity
          obstacles.current.forEach((obs) => {
            const top = getCactusTop(obs)
            if (!top) return
            const dx = top.x - playerX.current
            if (dir > 0 ? dx > 0 : dx < 0) {
              const dist = Math.abs(dx)
              if (dist < GRAPPLE_RANGE && dist < bestDist) {
                best = top
                bestDist = dist
              }
            }
          })
          if (best) {
            grapple.current = { targetX: best.x, targetY: best.y }
          }
        }
      }
      if (e.code === 'ArrowLeft' || e.code === 'ArrowRight') {
        e.preventDefault()
        keysDown.current[e.code] = true
      }
    }
    const onKeyUp = (e) => {
      if (e.code === 'ArrowLeft' || e.code === 'ArrowRight') {
        keysDown.current[e.code] = false
      }
    }
    document.addEventListener('keydown', onKeyDown)
    document.addEventListener('keyup', onKeyUp)
    return () => {
      document.removeEventListener('keydown', onKeyDown)
      document.removeEventListener('keyup', onKeyUp)
    }
  }, [startGame, nextLevel])

  // game loop
  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')

    const toScreen = (worldX, cameraX) => worldX - cameraX

    const drawGround = (cameraX, lvlConfig) => {
      ctx.fillStyle = lvlConfig.groundColor
      const leftEdge = 0
      const rightEdge = GAME_WIDTH
      let segments = [{ start: leftEdge, end: rightEdge }]

      obstacles.current.forEach((obs) => {
        if (obs.type === 'canyon') {
          const canyonLeft = toScreen(obs.x, cameraX)
          const canyonRight = toScreen(obs.x + 60, cameraX)
          if (canyonRight < 0 || canyonLeft > GAME_WIDTH) return
          const newSegments = []
          segments.forEach((seg) => {
            if (canyonLeft >= seg.end || canyonRight <= seg.start) {
              newSegments.push(seg)
            } else {
              if (seg.start < canyonLeft) {
                newSegments.push({ start: seg.start, end: canyonLeft })
              }
              if (canyonRight < seg.end) {
                newSegments.push({ start: canyonRight, end: seg.end })
              }
            }
          })
          segments = newSegments
        }
      })

      segments.forEach((seg) => {
        ctx.fillRect(seg.start, GROUND_Y, seg.end - seg.start, GAME_HEIGHT - GROUND_Y)
      })

      obstacles.current.forEach((obs) => {
        if (obs.type === 'canyon') {
          const screenX = toScreen(obs.x, cameraX)
          if (screenX + 60 < 0 || screenX > GAME_WIDTH) return
          ctx.fillStyle = lvlConfig.canyonWallColor
          ctx.fillRect(screenX, GROUND_Y, 4, GAME_HEIGHT - GROUND_Y)
          ctx.fillRect(screenX + 56, GROUND_Y, 4, GAME_HEIGHT - GROUND_Y)
          ctx.fillStyle = lvlConfig.groundColor
        }
      })
    }

    const drawMountain = (screenX, tall) => {
      const height = tall ? 70 : 45
      const width = tall ? 60 : 50
      ctx.fillStyle = '#6b5b3e'
      ctx.beginPath()
      ctx.moveTo(screenX, GROUND_Y)
      ctx.lineTo(screenX + width / 2, GROUND_Y - height)
      ctx.lineTo(screenX + width, GROUND_Y)
      ctx.closePath()
      ctx.fill()

      ctx.fillStyle = '#ffffff'
      ctx.beginPath()
      ctx.moveTo(screenX + width / 2 - 8, GROUND_Y - height + 18)
      ctx.lineTo(screenX + width / 2, GROUND_Y - height)
      ctx.lineTo(screenX + width / 2 + 8, GROUND_Y - height + 18)
      ctx.closePath()
      ctx.fill()
    }

    const drawCactus = (screenX, tall) => {
      const height = tall ? 75 : 50
      const trunkW = 12
      const cx = screenX + 25
      // trunk
      ctx.fillStyle = '#2d6b30'
      ctx.fillRect(cx - trunkW / 2, GROUND_Y - height, trunkW, height)
      // left arm
      const armY = GROUND_Y - height * 0.6
      ctx.fillRect(cx - trunkW / 2 - 16, armY, 16, 8)
      ctx.fillRect(cx - trunkW / 2 - 16, armY - 18, 8, 18)
      // right arm
      const armY2 = GROUND_Y - height * 0.75
      ctx.fillRect(cx + trunkW / 2, armY2, 16, 8)
      ctx.fillRect(cx + trunkW / 2 + 8, armY2 - 14, 8, 14)
    }

    const drawPlayer = (screenX, y, dead, right) => {
      const size = 36
      ctx.font = `${size}px serif`
      ctx.textAlign = 'center'
      ctx.textBaseline = 'bottom'
      ctx.save()
      ctx.translate(screenX, y)
      if (right) ctx.scale(-1, 1)
      if (dead) ctx.rotate(Math.PI / 2)
      ctx.fillText('\u{1F3C3}', 0, 0)
      ctx.restore()
    }

    const drawSword = (screenX, y, right, swingProgress) => {
      // swingProgress: 1 = start, 0 = end
      const angle = (1 - swingProgress) * Math.PI * 0.8 - Math.PI * 0.3
      const dir = right ? 1 : -1
      ctx.save()
      ctx.translate(screenX + dir * 10, y - 20)
      ctx.scale(dir, 1)
      ctx.rotate(angle)
      ctx.font = '28px serif'
      ctx.textAlign = 'center'
      ctx.textBaseline = 'middle'
      ctx.fillText('\u{1F5E1}', 20, 0)
      ctx.restore()
    }

    const drawBackground = (cameraX, lvlConfig) => {
      const grad = ctx.createLinearGradient(0, 0, 0, GROUND_Y)
      grad.addColorStop(0, lvlConfig.sky.top)
      grad.addColorStop(1, lvlConfig.sky.bottom)
      ctx.fillStyle = grad
      ctx.fillRect(0, 0, GAME_WIDTH, GAME_HEIGHT)

      if (lvlConfig.stars) {
        ctx.fillStyle = '#ffffff'
        for (let i = 0; i < 30; i++) {
          const sx = ((i * 97 + 13) - cameraX * 0.1) % GAME_WIDTH
          const sy = (i * 53 + 7) % (GROUND_Y - 40)
          const wrappedX = ((sx % GAME_WIDTH) + GAME_WIDTH) % GAME_WIDTH
          ctx.fillRect(wrappedX, sy, 2, 2)
        }
      } else if (lvlConfig.weapon === 'grapple') {
        // setting sun
        ctx.fillStyle = '#FF6347'
        ctx.beginPath()
        ctx.arc(680, GROUND_Y - 40, 50, 0, Math.PI * 2)
        ctx.fill()
        ctx.fillStyle = '#FF8C00'
        ctx.beginPath()
        ctx.arc(680, GROUND_Y - 40, 38, 0, Math.PI * 2)
        ctx.fill()
      } else if (lvlConfig.boss) {
        // forest trees in background
        for (let i = 0; i < 8; i++) {
          const tx = i * 110 + 30
          // trunk
          ctx.fillStyle = '#3d2b1f'
          ctx.fillRect(tx - 6, GROUND_Y - 120, 12, 120)
          // canopy
          ctx.fillStyle = '#145214'
          ctx.beginPath()
          ctx.arc(tx, GROUND_Y - 130, 35, 0, Math.PI * 2)
          ctx.fill()
          ctx.beginPath()
          ctx.arc(tx, GROUND_Y - 160, 28, 0, Math.PI * 2)
          ctx.fill()
        }
      } else {
        // sun
        ctx.fillStyle = '#FFD700'
        ctx.beginPath()
        ctx.arc(650, 60, 35, 0, Math.PI * 2)
        ctx.fill()
      }
    }

    const drawHUD = (lvlConfig) => {
      ctx.fillStyle = '#ffffff'
      ctx.font = 'bold 20px monospace'
      ctx.textAlign = 'right'
      ctx.textBaseline = 'top'
      const displayScore = Math.floor(maxDistance.current / 10)
      ctx.fillText(`Score: ${displayScore}`, GAME_WIDTH - 20, 15)

      ctx.textAlign = 'left'
      if (!lvlConfig.boss) {
        ctx.fillText(`\u{1FA99} ${coinCount.current} / ${COINS_TO_WIN[level.current]}`, 20, 15)
      }

      ctx.textAlign = 'center'
      ctx.fillText(LEVELS[level.current].name, GAME_WIDTH / 2, 15)

      if (lvlConfig.weapon === 'sword') {
        ctx.textAlign = 'left'
        const ready = swordSwing.current <= 0 ? 'READY' : ''
        ctx.fillText(`\u{1F5E1} ${ready}`, 20, 42)
      }
      if (lvlConfig.weapon === 'grenade') {
        ctx.textAlign = 'left'
        ctx.fillText(`\u{1F4A3} C to throw`, 20, 42)
      }
      if (lvlConfig.weapon === 'grapple') {
        ctx.textAlign = 'left'
        const status = grapple.current ? 'HOOKED' : 'READY'
        ctx.fillText(`\u{1FA9D} ${status}`, 20, 42)
      }
    }

    const getCollisionBox = (obs) => {
      if (obs.type === 'canyon') {
        return { type: 'canyon', left: obs.x + 8, right: obs.x + 52, top: GROUND_Y }
      } else if (obs.type === 'tall_mountain' || obs.type === 'tall_cactus') {
        return { type: 'solid', left: obs.x + 10, right: obs.x + 50, top: GROUND_Y - 70 }
      } else {
        return { type: 'solid', left: obs.x + 10, right: obs.x + 40, top: GROUND_Y - 45 }
      }
    }

    const PLAYER_SCREEN_X = GAME_WIDTH / 2

    const gameLoop = () => {
      const lvlConfig = LEVELS[level.current]
      const cameraX = lvlConfig.fixedScreen ? 0 : playerX.current - PLAYER_SCREEN_X

      if (gameStateRef.current !== 'running') {
        drawBackground(0, lvlConfig)
        drawGround(0, lvlConfig)
        drawPlayer(PLAYER_SCREEN_X, GROUND_Y, gameStateRef.current === 'dead', true)

        ctx.fillStyle = '#ffffff'
        ctx.strokeStyle = '#000000'
        ctx.lineWidth = 3
        ctx.font = 'bold 28px monospace'
        ctx.textAlign = 'center'
        ctx.textBaseline = 'middle'
        if (gameStateRef.current === 'idle') {
          ctx.fillText('Press SPACE to Run!', GAME_WIDTH / 2, GAME_HEIGHT / 2 - 40)
          ctx.font = '18px monospace'
          if (lvlConfig.boss) {
            ctx.fillText('Defeat the boss!', GAME_WIDTH / 2, GAME_HEIGHT / 2)
          } else {
            ctx.fillText(`Collect ${COINS_TO_WIN[level.current]} coins to win!`, GAME_WIDTH / 2, GAME_HEIGHT / 2)
          }
        } else if (gameStateRef.current === 'levelComplete') {
          ctx.fillText('Level Complete!', GAME_WIDTH / 2, GAME_HEIGHT / 2 - 60)
          ctx.font = '22px monospace'
          ctx.fillText(`Get ready for ${LEVELS[level.current + 1].name}`, GAME_WIDTH / 2, GAME_HEIGHT / 2 - 20)
          ctx.fillText('Press SPACE to continue', GAME_WIDTH / 2, GAME_HEIGHT / 2 + 20)
        } else if (gameStateRef.current === 'won') {
          ctx.fillText('You Win!', GAME_WIDTH / 2, GAME_HEIGHT / 2 - 60)
          ctx.font = '22px monospace'
          ctx.fillText('All levels complete!', GAME_WIDTH / 2, GAME_HEIGHT / 2 - 20)
          ctx.fillText('Press SPACE to play again', GAME_WIDTH / 2, GAME_HEIGHT / 2 + 20)
        } else {
          ctx.fillText('Game Over!', GAME_WIDTH / 2, GAME_HEIGHT / 2 - 60)
          ctx.font = '22px monospace'
          ctx.fillText(`Coins: ${coinCount.current}/${COINS_TO_WIN[level.current]}`, GAME_WIDTH / 2, GAME_HEIGHT / 2 - 20)
          ctx.fillText('Press SPACE to try again', GAME_WIDTH / 2, GAME_HEIGHT / 2 + 20)
        }
        frameRef.current = requestAnimationFrame(gameLoop)
        return
      }

      // horizontal movement
      if (keysDown.current['ArrowRight']) {
        playerX.current += MOVE_SPEED
        facingRight.current = true
      }
      if (keysDown.current['ArrowLeft']) {
        playerX.current -= MOVE_SPEED
        facingRight.current = false
      }

      // clamp player to screen on fixed levels
      if (lvlConfig.fixedScreen) {
        playerX.current = Math.max(20, Math.min(GAME_WIDTH - 20, playerX.current))
      }

      if (playerX.current > maxDistance.current) {
        maxDistance.current = playerX.current
      }

      // vertical physics
      velocityY.current += GRAVITY
      playerY.current += velocityY.current

      if (playerY.current >= GROUND_Y) {
        let overCanyon = false
        obstacles.current.forEach((obs) => {
          if (obs.type === 'canyon') {
            const box = getCollisionBox(obs)
            if (playerX.current + 12 > box.left && playerX.current - 12 < box.right) {
              overCanyon = true
            }
          }
        })
        // check craters (boss level)
        craters.current.forEach((crater) => {
          if (playerX.current + 12 > crater.x + 8 && playerX.current - 12 < crater.x + CRATER_WIDTH - 8) {
            overCanyon = true
          }
        })

        if (overCanyon) {
          if (playerY.current > GAME_HEIGHT + 50) {
            die()
          }
        } else {
          playerY.current = GROUND_Y
          velocityY.current = 0
          isJumping.current = false
        }
      }

      // check obstacle collisions (skip for grapple levels - cacti are grapple points)
      if (lvlConfig.weapon !== 'grapple') {
        obstacles.current.forEach((obs) => {
          if (obs.type !== 'canyon') {
            const box = getCollisionBox(obs)
            if (
              playerX.current + 12 > box.left &&
              playerX.current - 12 < box.right &&
              playerY.current > box.top
            ) {
              die()
            }
          }
        })
      }

      // grapple physics
      if (grapple.current) {
        const dx = grapple.current.targetX - playerX.current
        const dy = grapple.current.targetY - playerY.current
        const dist = Math.sqrt(dx * dx + dy * dy)

        if (dist < 30) {
          // reached the cactus top — launch!
          const launchDir = dx > 0 ? 1 : -1
          velocityY.current = GRAPPLE_LAUNCH_VY
          playerX.current += launchDir * GRAPPLE_LAUNCH_VX
          isJumping.current = true
          grapple.current = null
        } else {
          // pull toward target
          playerX.current += (dx / dist) * GRAPPLE_PULL_SPEED
          playerY.current += (dy / dist) * GRAPPLE_PULL_SPEED
          velocityY.current = 0 // override gravity while grappling
        }
      }

      // sword swing tick
      if (swordSwing.current > 0) {
        swordSwing.current -= 1
      }

      // boss AI
      if (boss.current && lvlConfig.boss) {
        const b = boss.current
        b.timer -= 1

        if (b.phase === 'walking') {
          b.x += BOSS_SPEED * b.dir
          // bounce off edges
          if (b.x < 100) b.dir = 1
          if (b.x > GAME_WIDTH - 100) b.dir = -1
          if (b.timer <= 0) {
            b.phase = 'winding_up'
            b.timer = 50
            // target where the player is
            b.punchX = playerX.current - CRATER_WIDTH / 2
            b.fistY = 0
          }
        } else if (b.phase === 'winding_up') {
          // boss shakes/raises fist
          b.fistY = Math.sin(b.timer * 0.5) * 10 - 30
          if (b.timer <= 0) {
            b.phase = 'punching'
            b.timer = 12
            b.fistY = 0
          }
        } else if (b.phase === 'punching') {
          // fist comes down
          b.fistY = (1 - b.timer / 12) * 200
          if (b.timer <= 0) {
            // create crater at punch location
            craters.current.push({ x: b.punchX })
            if (craters.current.length > 3) {
              craters.current.shift()
            }
            b.phase = 'recovering'
            b.timer = 80
          }
        } else if (b.phase === 'recovering') {
          b.fistY = 200
          if (b.timer <= 0) {
            b.phase = 'walking'
            b.timer = 120 + Math.floor(Math.random() * 80)
            b.fistY = 0
          }
        }
      }

      // grenade physics
      grenades.current = grenades.current.filter((g) => {
        g.x += g.vx
        g.vy += GRENADE_GRAVITY
        g.y += g.vy
        // land on ground
        if (g.y >= GROUND_Y - 10) {
          g.y = GROUND_Y - 10
          g.vy = 0
          g.vx *= 0.85 // friction
          if (Math.abs(g.vx) < 0.3) g.vx = 0
        }
        g.fuse -= 1
        if (g.fuse <= 0) {
          // detonate — check if boss is in blast radius
          if (boss.current && lvlConfig.boss) {
            const dx = Math.abs(g.x - boss.current.x)
            if (dx < GRENADE_BLAST_RADIUS) {
              boss.current.hp -= 1
              if (boss.current.hp <= 0) {
                boss.current = null
                win()
              }
            }
          }
          return false // remove grenade
        }
        return true
      })

      // move arrows and check collisions with obstacles (bow levels)
      if (lvlConfig.weapon === 'bow') {
        const ARROW_SPEED = 10
        arrows.current = arrows.current
          .map((a) => ({ ...a, x: a.x + ARROW_SPEED * a.dir }))
          .filter((a) => Math.abs(a.x - playerX.current) < GAME_WIDTH)

        obstacles.current = obstacles.current.filter((obs) => {
          if (obs.type === 'canyon') return true
          const box = getCollisionBox(obs)
          const hit = arrows.current.some(
            (a) => a.x > box.left && a.x < box.right && a.y > box.top && a.y < GROUND_Y,
          )
          if (hit) {
            arrows.current = arrows.current.filter(
              (a) => !(a.x > box.left && a.x < box.right && a.y > box.top && a.y < GROUND_Y),
            )
          }
          return !hit
        })
      }

      // move enemies (only on levels with enemies)
      if (lvlConfig.hasEnemies) {
      enemies.current.forEach((enemy) => {
        const speed = enemy.type === 'zombie' ? ZOMBIE_SPEED : BIRD_SPEED
        enemy.x += speed * enemy.dir
      })

      // check weapon hits on enemies
      if (lvlConfig.weapon === 'bow') {
        enemies.current = enemies.current.filter((enemy) => {
          const hitRadiusY = enemy.type === 'zombie' ? 36 : ENEMY_HIT_RADIUS
          const hit = arrows.current.some(
            (a) =>
              Math.abs(a.x - enemy.x) < ENEMY_HIT_RADIUS &&
              a.y > enemy.y - hitRadiusY &&
              a.y < enemy.y,
          )
          if (hit) {
            arrows.current = arrows.current.filter(
              (a) =>
                !(
                  Math.abs(a.x - enemy.x) < ENEMY_HIT_RADIUS &&
                  a.y > enemy.y - hitRadiusY &&
                  a.y < enemy.y
                ),
            )
          }
          return !hit
        })
      } else if (lvlConfig.weapon === 'sword' && swordSwing.current > 0) {
        const swordDir = facingRight.current ? 1 : -1
        enemies.current = enemies.current.filter((enemy) => {
          const dx = enemy.x - playerX.current
          const inFront = swordDir > 0 ? dx > 0 && dx < SWORD_RANGE : dx < 0 && dx > -SWORD_RANGE
          const dy = Math.abs(enemy.y - playerY.current)
          return !(inFront && dy < 50)
        })
      }

      // check enemy collisions with player
      enemies.current.forEach((enemy) => {
        const playerBottom = playerY.current
        const playerTop = playerBottom - 36
        const enemyBottom = enemy.y
        const enemyTop = enemy.y - 28
        if (
          Math.abs(playerX.current - enemy.x) < ENEMY_HIT_RADIUS &&
          playerBottom > enemyTop &&
          playerTop < enemyBottom
        ) {
          die()
        }
      })

      // spawn more enemies ahead
      const furthestEnemy = enemies.current.reduce(
        (max, e) => Math.max(max, e.x),
        playerX.current,
      )
      if (furthestEnemy < playerX.current + GAME_WIDTH * 1.5) {
        enemies.current.push(...generateEnemies(playerX.current))
      }
      enemies.current = enemies.current.filter(
        (e) => Math.abs(e.x - playerX.current) < GAME_WIDTH * 3,
      )
      } // end hasEnemies

      // collect coins (skip for boss levels)
      if (!lvlConfig.boss) {
      coins.current = coins.current.filter((coin) => {
        const playerBottom = playerY.current
        const playerTop = playerBottom - 36
        if (
          Math.abs(playerX.current - coin.x) < COIN_RADIUS &&
          playerBottom > coin.y - COIN_RADIUS &&
          playerTop < coin.y + COIN_RADIUS
        ) {
          coinCount.current += 1
          return false
        }
        return true
      })

      if (coinCount.current >= COINS_TO_WIN[level.current]) {
        win()
      }

      // spawn more coins ahead
      const furthestCoin = coins.current.reduce(
        (max, c) => Math.max(max, c.x),
        playerX.current,
      )
      if (furthestCoin < playerX.current + GAME_WIDTH * 1.5) {
        coins.current.push(...generateCoins(playerX.current))
      }
      coins.current = coins.current.filter(
        (c) => c.x > playerX.current - GAME_WIDTH,
      )

      } // end !boss coins

      // generate more obstacles ahead
      if (!lvlConfig.boss) {
      const rightmost = obstacles.current.reduce(
        (max, obs) => Math.max(max, obs.x),
        0,
      )
      if (rightmost < playerX.current + GAME_WIDTH) {
        const gap = 250 + Math.random() * 200
        obstacles.current.push(createObstacle(rightmost + gap, level.current))
      }
      } // end !boss obstacles

      // draw everything
      drawBackground(cameraX, lvlConfig)
      drawGround(cameraX, lvlConfig)

      obstacles.current.forEach((obs) => {
        const screenX = toScreen(obs.x, cameraX)
        if (screenX + 80 < 0 || screenX > GAME_WIDTH + 20) return
        if (obs.type === 'mountain') drawMountain(screenX, false)
        if (obs.type === 'tall_mountain') drawMountain(screenX, true)
        if (obs.type === 'cactus') drawCactus(screenX, false)
        if (obs.type === 'tall_cactus') drawCactus(screenX, true)
      })

      // draw coins
      coins.current.forEach((coin) => {
        const sx = toScreen(coin.x, cameraX)
        if (sx < -20 || sx > GAME_WIDTH + 20) return
        ctx.font = '24px serif'
        ctx.textAlign = 'center'
        ctx.textBaseline = 'middle'
        ctx.fillText('\u{1FA99}', sx, coin.y)
      })

      // draw enemies
      enemies.current.forEach((enemy) => {
        const sx = toScreen(enemy.x, cameraX)
        if (sx < -40 || sx > GAME_WIDTH + 40) return
        ctx.font = '30px serif'
        ctx.textAlign = 'center'
        ctx.textBaseline = 'bottom'
        ctx.save()
        ctx.translate(sx, enemy.y)
        if (enemy.dir < 0) ctx.scale(-1, 1)
        ctx.fillText(enemy.type === 'zombie' ? '\u{1F9DF}' : '\u{1F985}', 0, 0)
        ctx.restore()
      })

      // draw craters
      craters.current.forEach((crater) => {
        // cut out the ground
        ctx.fillStyle = '#000000'
        ctx.fillRect(crater.x, GROUND_Y, CRATER_WIDTH, GAME_HEIGHT - GROUND_Y)
        // crater walls
        ctx.fillStyle = lvlConfig.canyonWallColor
        ctx.fillRect(crater.x, GROUND_Y, 4, GAME_HEIGHT - GROUND_Y)
        ctx.fillRect(crater.x + CRATER_WIDTH - 4, GROUND_Y, 4, GAME_HEIGHT - GROUND_Y)
      })

      // draw boss
      if (boss.current && lvlConfig.boss) {
        const b = boss.current
        // boss body
        ctx.font = `${BOSS_SIZE}px serif`
        ctx.textAlign = 'center'
        ctx.textBaseline = 'bottom'
        ctx.save()
        ctx.translate(b.x, GROUND_Y)
        if (b.dir < 0) ctx.scale(-1, 1)
        ctx.fillText('\u{1F9DF}', 0, 0)
        ctx.restore()

        // boss fist during punch
        if (b.phase === 'winding_up' || b.phase === 'punching' || b.phase === 'recovering') {
          const fistScreenX = b.punchX + CRATER_WIDTH / 2
          const fistScreenY = Math.min(GROUND_Y - 20, 80 + b.fistY)
          ctx.font = '50px serif'
          ctx.textAlign = 'center'
          ctx.textBaseline = 'bottom'
          ctx.fillText('\u{1F44A}', fistScreenX, fistScreenY)
        }

        // health bar
        const barW = 200
        const barH = 16
        const barX = GAME_WIDTH / 2 - barW / 2
        const barY = GAME_HEIGHT - 30
        ctx.fillStyle = '#333'
        ctx.fillRect(barX, barY, barW, barH)
        ctx.fillStyle = '#e22'
        ctx.fillRect(barX, barY, barW * (b.hp / 10), barH)
        ctx.strokeStyle = '#fff'
        ctx.lineWidth = 2
        ctx.strokeRect(barX, barY, barW, barH)
        ctx.fillStyle = '#fff'
        ctx.font = 'bold 12px monospace'
        ctx.textAlign = 'center'
        ctx.textBaseline = 'middle'
        ctx.fillText('BOSS', GAME_WIDTH / 2, barY + barH / 2)
      }

      // draw grenades
      grenades.current.forEach((g) => {
        const gx = lvlConfig.fixedScreen ? g.x : toScreen(g.x, cameraX)
        // flash faster as fuse runs out
        const flash = g.fuse < 60 ? Math.floor(g.fuse / 4) % 2 === 0 : false
        ctx.font = '22px serif'
        ctx.textAlign = 'center'
        ctx.textBaseline = 'middle'
        ctx.fillText(flash ? '\u{1F4A5}' : '\u{1F4A3}', gx, g.y)
        // fuse timer text
        const seconds = (g.fuse / 60).toFixed(1)
        ctx.font = 'bold 12px monospace'
        ctx.fillStyle = g.fuse < 60 ? '#ff4444' : '#ffffff'
        ctx.fillText(seconds, gx, g.y - 18)
        ctx.fillStyle = '#ffffff'
      })

      // draw arrows (bow levels)
      if (lvlConfig.weapon === 'bow') {
        arrows.current.forEach((a) => {
          const sx = toScreen(a.x, cameraX)
          if (sx < -20 || sx > GAME_WIDTH + 20) return
          ctx.font = '20px serif'
          ctx.textAlign = 'center'
          ctx.textBaseline = 'middle'
          ctx.save()
          ctx.translate(sx, a.y)
          if (a.dir < 0) ctx.scale(-1, 1)
          ctx.fillText('\u{1F3F9}', 0, 0)
          ctx.restore()
        })
      }

      // draw grapple rope
      if (grapple.current) {
        const targetSX = toScreen(grapple.current.targetX, cameraX)
        ctx.strokeStyle = '#a0522d'
        ctx.lineWidth = 3
        ctx.beginPath()
        ctx.moveTo(lvlConfig.fixedScreen ? playerX.current : PLAYER_SCREEN_X, playerY.current - 18)
        ctx.lineTo(targetSX, grapple.current.targetY)
        ctx.stroke()
        // hook
        ctx.font = '16px serif'
        ctx.textAlign = 'center'
        ctx.textBaseline = 'middle'
        ctx.fillText('\u{1FA9D}', targetSX, grapple.current.targetY)
      }

      const playerDrawX = lvlConfig.fixedScreen ? playerX.current : PLAYER_SCREEN_X
      drawPlayer(playerDrawX, playerY.current, false, facingRight.current)

      // draw sword swing
      if (lvlConfig.weapon === 'sword' && swordSwing.current > 0) {
        const progress = swordSwing.current / SWORD_DURATION
        drawSword(playerDrawX, playerY.current, facingRight.current, progress)
      }

      drawHUD(lvlConfig)

      frameRef.current = requestAnimationFrame(gameLoop)
    }

    frameRef.current = requestAnimationFrame(gameLoop)
    return () => {
      if (frameRef.current) cancelAnimationFrame(frameRef.current)
    }
  }, [die, win, gameState])

  return (
    <Box
      sx={{
        height: '100%',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 2,
      }}
    >
      <canvas
        ref={canvasRef}
        width={GAME_WIDTH}
        height={GAME_HEIGHT}
        style={{
          border: '2px solid #555',
          borderRadius: 8,
          cursor: 'pointer',
        }}
        onClick={() => {
          if (gameStateRef.current === 'levelComplete') {
            nextLevel()
          } else if (gameStateRef.current !== 'running') {
            startGame()
          } else if (!isJumping.current) {
            velocityY.current = JUMP_FORCE
            isJumping.current = true
          }
        }}
      />
      {highScore > 0 && (
        <Box sx={{ color: 'text.secondary', fontSize: '1.2em' }}>
          High Score: {highScore}
        </Box>
      )}
      <Box sx={{ color: 'text.secondary', fontSize: '0.9em' }}>
        Arrow keys to move, SPACE or UP to jump, C to attack
      </Box>
    </Box>
  )
}

export default RunGame
